<template>
  <v-app class="app-tabela">
    <div class="resultado-datatable-filtros">
      <ResultadoToggle
        v-model="tipoResultado"
        :disabledMensal="tipoPrevistoRealizado === 'realizado'"
      />
    </div>

    <div class="box mt-0 mb-5">
      <v-card class="tabela">
        <v-card-title>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar por nome da equipe, CNES ou INE"
            class="input-bottom"
            single-line
            hide-details
          />
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="indexedItems"
          :loading="loading"
          loading-text="Carregando, Por favor espere"
          :search="search"
          :item-class="getRowColor"
          :footer-props="{
            itemsPerPageText: '',
            'items-per-page-options': [10],
            'disable-items-per-page': true,
          }"
          :page="currentPage"
          item-key="index"
          show-expand
          :single-expand="true"
          :expanded.sync="expanded"
          @pagination="getPagination"
          @item-expanded="onItemExpanded"
          class="elevation-1"
        >
          <template v-slot:[`footer.page-text`]="items">
            <div class="info-table-item tabela-esus-pec"></div>
            <span class="paginacao">
              {{ items.pageStart }} - {{ items.pageStop }} de
              {{ items.itemsLength }} equipes
            </span>
          </template>

          <template v-slot:item.nota="{ item }">
            {{ getValorIndicador(item, 'nota') }}
          </template>

          <template v-slot:item.classificacao="{ item }">
            <TextoClassificacaoIndicador
              :classificacao="getValorIndicador(item, 'classificacao')"
              :idIndicador="indicador"
              :resultado="getValorIndicador(item, 'resultado')"
            />
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <table class="table table-interna">
                <thead>
                  <tr>
                    <th>Indicador</th>
                    <th>Peso</th>
                    <th>Numerador</th>
                    <th>Denominador</th>
                    <th>Pontuação</th>
                    <th>Nota</th>
                    <th>Classificação</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>
                      <router-link :to="linksCards(indicador)">
                        {{ item.ds_indicador }}
                      </router-link>
                    </td>
                    <td>
                      {{ item.peso ?? '' }}
                    </td>

                    <td>
                      <template v-if="[47, 54, 55].includes(Number(indicador))">
                        -
                      </template>
                      <template v-else>
                        {{ getValorIndicador(item, 'numerador') }}
                        <span
                          v-if="getValorIndicador(item, 'numerador') !== '-'"
                          v-b-tooltip.html.hover
                          :title="getTooltipsTexts(indicador).numerador"
                          class="icon-tooltip"
                        >
                          <i class="icofont-info-circle"></i>
                        </span>
                      </template>
                    </td>

                    <td>
                      <template v-if="[54, 55].includes(Number(indicador))">
                        -
                      </template>

                      <template v-else>
                        {{ getValorIndicador(item, 'denominador') }}

                        <span
                          v-if="getValorIndicador(item, 'denominador') !== '-'"
                          v-b-tooltip.html.hover
                          :title="getTooltipsTexts(indicador).denominador"
                          class="icon-tooltip"
                        >
                          <i class="icofont-info-circle"></i>
                        </span>
                      </template>
                    </td>

                    <td>
                      {{ getValorIndicador(item, 'resultado') }}

                      <span
                        v-if="getValorIndicador(item, 'resultado') !== '-'"
                        v-b-tooltip.html.hover
                        :title="getTooltipsTexts(indicador).resultado"
                        class="icon-tooltip"
                      >
                        <i class="icofont-info-circle"></i>
                      </span>
                    </td>

                    <td>
                      {{ getValorIndicador(item, 'nota') }}
                    </td>

                    <td>
                      <TextoClassificacaoIndicador
                        :classificacao="
                          getValorIndicador(item, 'classificacao')
                        "
                        :idIndicador="indicador"
                        :resultado="getValorIndicador(item, 'resultado')"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </template>

          <template v-slot:progress>
            <v-progress-linear color="blue" :height="10" indeterminate />
          </template>

          <template v-slot:no-data>
            <div class="alert alert-info mb-0">
              <b>Nenhuma informação encontrada. Selecione outro filtro.</b>
            </div>
          </template>

          <template v-slot:no-results>
            <div class="alert alert-info mb-0">
              <b>Nenhuma informação encontrada. Selecione outro filtro.</b>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-app>
</template>

<script>
import TextoClassificacaoIndicador from '@/components/utils/TextoClassificacaoIndicador'
import ResultadoToggle from '@/pages/IndicadoresAps/ResultadoMunicipal/components/ResultadoToggle.vue'

export default {
  name: 'IndicadoresDataTable',
  components: {
    TextoClassificacaoIndicador,
    ResultadoToggle,
  },
  props: {
    items: { type: Array, default: () => [] },
    loading: {
      type: Boolean,
      default: false,
    },
    indicador: {
      type: [Number, String],
      default: null,
    },
    tipoPrevistoRealizado: {
      type: String,
      default: 'previsto',
    },
  },
  data() {
    return {
      search: '',
      currentPage: 1,
      expanded: [],
      headers: [
        {
          text: 'CNES',
          align: 'start',
          filterable: true,
          value: 'nu_cnes',
        },
        {
          text: 'UBS',
          align: 'start',
          filterable: true,
          value: 'no_unidade_saude',
        },
        {
          text: 'INE',
          align: 'start',
          filterable: true,
          value: 'nu_ine',
        },
        {
          text: 'Equipe',
          align: 'start',
          filterable: true,
          value: 'no_equipe',
        },
        {
          text: 'Nota',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'nota',
        },
        {
          text: 'Classificação',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'classificacao',
        },
        { text: '', value: 'data-table-expand' },
      ],
      tipoResultado: 'quadrimestral',
    }
  },
  computed: {
    indexedItems() {
      return this.items.map((it, idx) => ({
        index: idx,
        ...it,
      }))
    },
  },
  watch: {
    /**
     * Reinicia a tabela ao receber outro indicador.
     *
     * @return {void}
     */
    indicador() {
      this.tipoResultado = 'quadrimestral'
      this.expanded = []
      this.search = ''
      this.currentPage = 1
    },

    /**
     * Mantém o modo quadrimestral ao exibir dados realizados.
     *
     * @param {String} tipo Tipo de resultado selecionado.
     * @return {void}
     */
    tipoPrevistoRealizado(tipo) {
      if (tipo === 'realizado') {
        this.tipoResultado = 'quadrimestral'
      }

      this.expanded = []
      this.currentPage = 1
    },
  },
  methods: {
    /**
     * Atualiza a página atual da tabela.
     * @param {Object} p Dados da paginação
     */
    getPagination(p) {
      this.currentPage = p.page
    },

    /**
     * Manipula o evento de expansão de um item da tabela.
     * @param {Object} ctx Contexto do item expandido
     */
    onItemExpanded(ctx) {},

    /**
     * Retorna a classe CSS da linha da tabela.
     *
     * @param {Object} item
     */
    getRowColor(item) {},

    /**
     * Retorna as informações para os tooltips
     *
     * @param idIndicador
     *
     * @returns {object}
     */
    getTooltipsTexts(idIndicador) {
      const nomesIndicadores = {
        46: {
          numerador:
            'Total de atendimentos por demanda programada (consulta agendada programada; cuidado continuado; e consulta agendada).',
          denominador:
            'Total de atendimentos por todos os tipos de demandas (espontâneas e programadas).',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 10 ou > 70',
            'Suficiente: > 10 e <= 30',
            'Bom: > 30 e <= 50',
            'Ótimo: > 50 e <= 70',
          ],
        },
        51: {
          numerador:
            'Somatório da(s) boa(s) prática(s) pontuada(s) durante o acompanhamento do desenvolvimento infantil.',
          denominador:
            'Total de crianças com idade igual a 2 (dois) anos completos.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        52: {
          numerador:
            'Somatório da(s) boa(s) prática(s) pontuada(s) durante o acompanhamento de uma pessoa gestante e puérpera.',
          denominador:
            'Total de mulheres com encerramento de puerpera no quadrimestre avaliado.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        48: {
          numerador:
            'Somatório da(s) boa(s) prática(s) pontuada(s) durante o acompanhamento da pessoa com diabetes.',
          denominador: 'Total de pessoas com diabetes.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        50: {
          numerador:
            'Somatório da(s) boa(s) prática(s) pontuada(s) durante o acompanhamento da pessoa com hipertensão arterial.',
          denominador: 'Total de pessoas com hipertensão arterial.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        49: {
          numerador:
            'Somatório da(s) boa(s) prática(s) pontuada(s) durante o acompanhamento da pessoa idosa.',
          denominador: 'Total de pessoas idosas.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        47: {
          numerador:
            'Somatório da(s) boa(s) prática(s) aplicável(is) para a mulher.',
          denominador: 'Total de mulheres elegíveis na faixa etária.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            'Ótimo: > 75 e <= 100',
          ],
        },
        44: {
          numerador:
            'Total de atendimentos individuais e coletivos realizados pela eMulti no quadrimestre avaliado.',
          denominador: 'Total de pessoas atendidas no quadrimestre avaliado.',
          resultado: 'Numerador / Denominador',
          classificacao: [
            'Regular: <= 1',
            'Suficiente: > 1 e <= 2',
            'Bom: > 2 e <= 3',
            'Ótimo: > 3',
          ],
        },
        45: {
          numerador:
            'Total de ações compartilhadas realizadas pela eMulti no quadrimestre avaliado.',
          denominador: 'Total de ações realizadas pela eMulti.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 1',
            'Suficiente: > 1 e <= 2,5',
            'Bom: > 2,5 e <= 5',
            'Ótimo: > 5',
          ],
        },
        38: {
          numerador:
            'Total de pessoas com atendimentos de primeira consulta odontológica programática.',
          denominador: 'Total de pessoas vinculadas à equipe.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 1',
            'Suficiente: > 1 e <= 3',
            'Bom: > 3 e <= 5',
            'Ótimo: > 5',
          ],
        },
        43: {
          numerador: 'Total de pessoas com tratamento odontológico concluído.',
          denominador:
            'Total de pessoas com primeira consulta odontológica programada.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 25',
            'Suficiente: > 25 e <= 50',
            'Bom: > 50 e <= 75',
            ' Ótimo: > 75 e <= 100',
          ],
        },
        39: {
          numerador: 'Total de exodontias.',
          denominador:
            'Total de procedimentos clínicos individuais preventivos, curativos e exodontias realizados.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: < 8 ou >= 14',
            'Suficiente: >= 12 e < 14',
            'Bom: >= 10 e < 12',
            'Ótimo: >= 8 e < 10',
          ],
        },
        42: {
          numerador:
            'Total de pessoas participantes da ação coletiva de escovação dental supervisionada realizada em crianças com idade entre 6 e 12 anos.',
          denominador: 'Total de pessoas vinculadas à equipe.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 0,25',
            'Suficiente: > 0,25 e <= 0,5',
            'Bom: > 0,5 e <= 1',
            'Ótimo: > 1',
          ],
        },
        40: {
          numerador:
            'Total de procedimentos odontológicos individuais preventivos.',
          denominador:
            'Todos os procedimentos odontológicos individuais registrados.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: < 40 ou > 85',
            'Suficiente: >= 40 e < 60',
            'Bom: >= 60 e < 80',
            'Ótimo: >= 80 e <= 85',
          ],
        },
        41: {
          numerador:
            'Total de procedimentos do tipo Tratamento Restaurador Atraumático.',
          denominador: 'Total de procedimentos restauradores.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [
            'Regular: <= 3',
            'Suficiente: > 3 e <= 6',
            'Bom: > 6 e <= 8',
            'Ótimo: > 8',
          ],
        },
        53: {
          numerador: 'Total de gestantes com atendimento odontológico.',
          denominador: 'Total de gestantes da equipe vinculada.',
          resultado: 'Numerador / Denominador * 100',
          classificacao: [],
        },
      }

      const indicador = nomesIndicadores[idIndicador]
      if (!indicador) return ''

      return {
        ...indicador,
        classificacao: indicador.classificacao.join('<br>'),
      }
    },

    /**
     * Retorna o link da rota do card
     *
     * @param {number} idIndicador
     * @returns {string}
     */
    linksCards(idIndicador) {
      const links = {
        46: '/indicadores-aps/esf-eap/mais-acesso-aps',
        51: '/indicadores-aps/esf-eap/desenvolvimento-infantil',
        52: '/indicadores-aps/esf-eap/gestantes-puerperas',
        48: '/indicadores-aps/esf-eap/pessoa-diabetes',
        50: '/indicadores-aps/esf-eap/pessoa-hipertensao',
        49: '/indicadores-aps/esf-eap/pessoa-idosa',
        47: '/indicadores-aps/esf-eap/saude-da-mulher',
        44: '/indicadores-aps/emulti/media-atendimentos-por-pessoa',
        45: '/indicadores-aps/emulti/acoes-interprofissionais',
        38: '/indicadores-aps/indicadores-esb/primeira-consulta-programada',
        43: '/indicadores-aps/indicadores-esb/tratamentos-concluidos',
        39: '/indicadores-aps/indicadores-esb/exodontias',
        42: '/indicadores-aps/indicadores-esb/escovacao-supervisionada',
        40: '/indicadores-aps/indicadores-esb/procedimentos-odonto-preventivos',
        41: '/indicadores-aps/indicadores-esb/tratamentos-restauradores-atramautico',
        53: '/indicadores-aps/indicadores-esb/saude-bucal-gestantes',
      }
      return links[idIndicador] || ''
    },

    /**
     * Retorna o valor do campo conforme o tipo de resultado selecionado
     * para cada indicador.
     *
     * Regras:
     * - Indicador 46:
     *   - Mensal: utiliza os campos *_mes.
     *   - Quadrimestral: utiliza os campos padrão.
     *
     * - Indicadores 51 e 52:
     *   - Mensal: utiliza os campos *_all.
     *   - Quadrimestral: utiliza os campos padrão.
     *
     * - Indicadores 47, 48, 49 e 50:
     *   - Sempre utilizam os campos padrão.
     *
     * - Indicadores 44, 45, 38, 39, 40, 41, 42 e 43:
     *   - Mensal: utiliza os campos padrão.
     *   - Quadrimestral: utiliza os campos *_quad.
     *
     * - Demais indicadores:
     *   - Utilizam os campos padrão.
     *
     * @param {Object} item Dados do indicador.
     * @param {String} campo Nome do campo (numerador, denominador, resultado, nota ou classificacao).
     * @returns {*} Valor do campo conforme o indicador e o tipo de resultado selecionado.
     */
    getValorIndicador(item, campo) {
      const indicador = Number(this.indicador)

      if (this.tipoPrevistoRealizado === 'realizado') {
        return item[campo] ?? '-'
      }

      // Indicador 46
      if (indicador === 46) {
        if (this.tipoResultado === 'mensal') {
          return item[`${campo}_mes`] ?? '-'
        }

        return item[campo] ?? '-'
      }

      // Indicadores 51 e 52
      if ([51, 52].includes(indicador)) {
        if (this.tipoResultado === 'mensal') {
          return item[`${campo}_all`] ?? '-'
        }

        return item[campo] ?? '-'
      }

      // Indicadores 47, 48, 49, 50
      if ([47, 48, 49, 50].includes(indicador)) {
        return item[campo] ?? '-'
      }

      // Indicadores 44 e 45
      if ([44, 45].includes(indicador)) {
        if (this.tipoResultado === 'mensal') {
          return item[campo] ?? '-'
        }

        return item[`${campo}_quad`] ?? '-'
      }

      // Indicadores 38, 39, 40, 41, 42, 43
      if ([38, 39, 40, 41, 42, 43].includes(indicador)) {
        if (this.tipoResultado === 'mensal') {
          return item[campo] ?? '-'
        }

        return item[`${campo}_quad`] ?? '-'
      }

      return item[campo] ?? '-'
    },
  },
}
</script>

<style scoped>
.resultado-datatable-filtros {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 14px;
}

@media (max-width: 576px) {
  .resultado-datatable-filtros {
    justify-content: center;
  }
}
</style>
